import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
} from "react";

const initialState = {
  amountWallet: 0,
  amountBankTransfer: 0,
  amountToPayWithExtracredit: 0,
  amountToPayWithCreditCard: 0,
  extracoinInWallet: 0,
  bankTransferReceipt: null,
  combinePayment: false,
  selectedPaymentMethod: null,
  loading: false,
  sendMethod: null,
  page: 1,
  address: "",
  city: "",
  country: "",
  district: "",
  number: "",
  postalCode: "",
  state: "",
};

// Ações
const PAYMENT_ACTIONS = {
  SET_AMOUNT_WALLET: "SET_AMOUNT_WALLET",
  SET_AMOUNT_BANK_TRANSFER: "SET_AMOUNT_BANK_TRANSFER",
  SET_AMOUNT_EXTRACREDIT: "SET_AMOUNT_EXTRACREDIT",
  SET_AMOUNT_CREDIT_CARD: "SET_AMOUNT_CREDIT_CARD",
  SET_EXTRACOIN_IN_WALLET: "SET_EXTRACOIN_IN_WALLET",
  SET_BANK_TRANSFER_RECEIPT: "SET_BANK_TRANSFER_RECEIPT",
  SET_COMBINE_PAYMENT: "SET_COMBINE_PAYMENT",
  SET_SELECTED_PAYMENT_METHOD: "SET_SELECTED_PAYMENT_METHOD",
  SET_LOADING: "SET_LOADING",
  RESET_PAYMENTS: "RESET_PAYMENTS",
  SET_SEND_METHOD: "SET_SEND_METHOD",
  SET_PAGE: "SET_PAGE",
  SET_ADDRESS: "SET_ADDRESS",
  SET_CITY: "SET_CITY",
  SET_COUNTRY: "SET_COUNTRY",
  SET_DISTRICT: "SET_DISTRICT",
  SET_NUMBER: "SET_NUMBER",
  SET_POSTAL_CODE: "SET_POSTAL_CODE",
  SET_STATE: "SET_STATE",
  SET_COMPLETE_ADDRESS: "SET_COMPLETE_ADDRESS",
};

// Reducer
function paymentReducer(state, action) {
  switch (action.type) {
    case PAYMENT_ACTIONS.SET_AMOUNT_WALLET:
      return { ...state, amountWallet: action.payload };
    case PAYMENT_ACTIONS.SET_AMOUNT_BANK_TRANSFER:
      return { ...state, amountBankTransfer: action.payload };
    case PAYMENT_ACTIONS.SET_AMOUNT_EXTRACREDIT:
      return { ...state, amountToPayWithExtracredit: action.payload };
    case PAYMENT_ACTIONS.SET_AMOUNT_CREDIT_CARD:
      if (action.payload > 0 && state.selectedPaymentMethod === "paypal") {
        return {
          ...state,
          amountToPayWithCreditCard: action.payload,
          selectedPaymentMethod: null,
        };
      }
      return { ...state, amountToPayWithCreditCard: action.payload };
    case PAYMENT_ACTIONS.SET_EXTRACOIN_IN_WALLET:
      return { ...state, extracoinInWallet: action.payload };
    case PAYMENT_ACTIONS.SET_BANK_TRANSFER_RECEIPT:
      return { ...state, bankTransferReceipt: action.payload };
    case PAYMENT_ACTIONS.SET_COMBINE_PAYMENT:
      return { ...state, combinePayment: action.payload };
    case PAYMENT_ACTIONS.SET_SELECTED_PAYMENT_METHOD:
      if (action.payload === "paypal") {
        return {
          ...state,
          selectedPaymentMethod: action.payload,
          amountToPayWithCreditCard: 0,
        };
      } else if (action.payload === "creditCard") {
        return {
          ...state,
          selectedPaymentMethod: action.payload,
        };
      }
      return { ...state, selectedPaymentMethod: action.payload };
    case PAYMENT_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case PAYMENT_ACTIONS.RESET_PAYMENTS:
      return { ...initialState };
    case PAYMENT_ACTIONS.SET_SEND_METHOD:
      return { ...state, sendMethod: action.payload };
    case PAYMENT_ACTIONS.SET_PAGE:
      return { ...state, page: action.payload };
    // Adicionar casos para endereço
    case PAYMENT_ACTIONS.SET_ADDRESS:
      return { ...state, address: action.payload };
    case PAYMENT_ACTIONS.SET_CITY:
      return { ...state, city: action.payload };
    case PAYMENT_ACTIONS.SET_COUNTRY:
      return { ...state, country: action.payload };
    case PAYMENT_ACTIONS.SET_DISTRICT:
      return { ...state, district: action.payload };
    case PAYMENT_ACTIONS.SET_NUMBER:
      return { ...state, number: action.payload };
    case PAYMENT_ACTIONS.SET_POSTAL_CODE:
      return { ...state, postalCode: action.payload };
    case PAYMENT_ACTIONS.SET_STATE:
      return { ...state, state: action.payload };
    case PAYMENT_ACTIONS.SET_COMPLETE_ADDRESS:
      return {
        ...state,
        address: action.payload.address,
        city: action.payload.city,
        country: action.payload.country,
        district: action.payload.district,
        number: action.payload.number,
        postalCode: action.payload.postalCode,
        state: action.payload.state,
      };
    default:
      return state;
  }
}

// Context
const PaymentContext = createContext();

// Provider
export const PaymentProvider = ({
  children,
  orderConfig,
  orderItems,
  initialPage,
  currencyFormat,
}) => {
  const [state, dispatch] = useReducer(paymentReducer, {
    ...initialState,
    page: initialPage || initialState.page,
  });

  useEffect(() => {
    if (orderConfig?.extracoinInWallet !== undefined) {
      dispatch({
        type: PAYMENT_ACTIONS.SET_EXTRACOIN_IN_WALLET,
        payload: orderConfig.extracoinInWallet,
      });
    }
  }, [orderConfig?.extracoinInWallet]);

  useEffect(() => {
    dispatch({
      type: PAYMENT_ACTIONS.SET_SEND_METHOD,
      payload: orderConfig?.sendMethod || null,
    });
  }, [orderConfig?.sendMethod]);

  useEffect(() => {
    if (orderConfig?.userInfo) {
      if (!state.country) {
        dispatch({
          type: PAYMENT_ACTIONS.SET_COUNTRY,
          payload: orderConfig.userInfo.country || "IT",
        });
      }
    }
  }, [orderConfig?.userInfo, state.country]);

  useEffect(() => {
    if (orderConfig?.userInfo) {
      const hasValidAddress =
        orderConfig.userInfo.address &&
        orderConfig.userInfo.city &&
        orderConfig.userInfo.country &&
        orderConfig.userInfo.district &&
        orderConfig.userInfo.number &&
        orderConfig.userInfo.postalCode &&
        orderConfig.userInfo.state;

      const hasExistingAddress =
        state.address ||
        state.city ||
        state.district ||
        state.number ||
        state.postalCode ||
        state.state;

      if (hasValidAddress && !hasExistingAddress) {
        dispatch({
          type: PAYMENT_ACTIONS.SET_COMPLETE_ADDRESS,
          payload: {
            address: orderConfig.userInfo.address || "",
            city: orderConfig.userInfo.city || "",
            country: orderConfig.userInfo.country || "IT",
            district: orderConfig.userInfo.district || "",
            number: orderConfig.userInfo.number || "",
            postalCode: orderConfig.userInfo.postalCode || "",
            state: orderConfig.userInfo.state || "",
          },
        });
      }
    }
  }, [orderConfig?.userInfo]);

  const calculateTotalPrice = useCallback(() => {
    if (!orderConfig) return 0;

    const total = orderConfig.orderTotal || 0;

    return parseFloat(total) || 0;
  }, [orderConfig, orderConfig?.orderTotal]);

  const calculateRemainingPrice = useCallback(() => {
    const totalPrice = calculateTotalPrice();

    if (state.combinePayment) {
      const totalPaid =
        state.amountWallet +
        state.amountBankTransfer +
        state.amountToPayWithExtracredit +
        state.amountToPayWithCreditCard;

      const remaining = Math.max(0, totalPrice - totalPaid);

      return remaining;
    } else {
      if (state.selectedPaymentMethod === "paypal") {
        return 0;
      } else if (
        state.selectedPaymentMethod === "bankTransfer" &&
        state.bankTransferReceipt !== null
      ) {
        return 0;
      } else if (
        state.selectedPaymentMethod === "extracredit" &&
        state.extracoinInWallet >= totalPrice
      ) {
        return 0;
      } else if (
        state.selectedPaymentMethod === "creditCard" &&
        state.amountToPayWithCreditCard === totalPrice
      ) {
        return 0;
      } else if (state.selectedPaymentMethod === "wallet" && state.amountWallet === totalPrice) {
        return 0;
      } else {
        return totalPrice;
      }
    }
  }, [
    state.combinePayment,
    state.selectedPaymentMethod,
    state.amountWallet,
    state.amountBankTransfer,
    state.amountToPayWithExtracredit,
    state.amountToPayWithCreditCard,
    state.bankTransferReceipt,
    state.extracoinInWallet,
    calculateTotalPrice,
  ]);

  const canProceed = useCallback(() => {
    const order = orderConfig.order;
    const hasFreight = order?.amountShipment && order.amountShipment > 0;
    const hasValidShippingQuote = orderConfig.sendMethod && orderConfig.sendMethod !== null;

    const isPickup = !hasValidShippingQuote && !hasFreight;

    if (hasFreight && !hasValidShippingQuote && !isPickup) {
      return false;
    }

    if (state.combinePayment) {
      const totalPaid =
        state.amountWallet +
        state.amountBankTransfer +
        state.amountToPayWithExtracredit +
        state.amountToPayWithCreditCard;
      const totalPrice = calculateTotalPrice();
      const totalMatches = parseFloat(totalPaid.toFixed(2)) === parseFloat(totalPrice.toFixed(2));

      const hasBankTransferWithoutReceipt =
        state.amountBankTransfer > 0 && !state.bankTransferReceipt;

      const canProceedResult = totalMatches && !hasBankTransferWithoutReceipt;

      return canProceedResult;
    } else {
      if (state.selectedPaymentMethod === null) {
        return false;
      }

      const totalPrice = calculateTotalPrice();
      let canProceedResult = false;

      if (state.selectedPaymentMethod === "bankTransfer") {
        const tolerance = 0.05;
        const amountDiff = Math.abs(state.amountBankTransfer - totalPrice);
        canProceedResult = amountDiff <= tolerance && state.bankTransferReceipt !== null;
      } else if (state.selectedPaymentMethod === "extracredit") {
        const tolerance = 0.05;
        const amountDiff = Math.abs(state.amountToPayWithExtracredit - totalPrice);
        canProceedResult = amountDiff <= tolerance && state.extracoinInWallet >= totalPrice;
      } else if (state.selectedPaymentMethod === "creditCard") {
        canProceedResult = false;
      } else if (state.selectedPaymentMethod === "wallet") {
        const tolerance = 0.05;
        const amountDiff = Math.abs(state.amountWallet - totalPrice);
        canProceedResult = amountDiff <= tolerance;
      } else if (state.selectedPaymentMethod === "paypal") {
        canProceedResult = false;
      }

      return canProceedResult;
    }
  }, [
    state.combinePayment,
    state.selectedPaymentMethod,
    state.amountWallet,
    state.amountBankTransfer,
    state.amountToPayWithExtracredit,
    state.amountToPayWithCreditCard,
    state.bankTransferReceipt,
    state.extracoinInWallet,
    calculateTotalPrice,
    orderConfig.order,
    orderConfig.sendMethod,
    orderConfig.orderTotal,
  ]);

  const getMaxAmounts = useCallback(() => {
    const totalPrice = calculateTotalPrice();
    const { walletBalanceForex, walletBalance, currencyCodeUser } = orderConfig;

    const orderCurrency = orderConfig.isFringeBenefitsPage
      ? orderConfig?.order?.fringeBenefit?.currency
      : orderItems?.[0]?.product?.currency || orderConfig.currencyProduct;

    const walletBalanceValue =
      currencyCodeUser !== orderCurrency ? walletBalanceForex : walletBalance;

    if (state.combinePayment) {
      const paidByOtherMethods =
        state.amountWallet +
        state.amountBankTransfer +
        state.amountToPayWithExtracredit +
        state.amountToPayWithCreditCard;

      const maxAmounts = {
        maxWallet: Math.min(
          walletBalanceValue,
          totalPrice -
            state.amountToPayWithExtracredit -
            state.amountBankTransfer -
            state.amountToPayWithCreditCard
        ),
        maxExtracredit: Math.min(
          state.extracoinInWallet,
          totalPrice -
            state.amountWallet -
            state.amountBankTransfer -
            state.amountToPayWithCreditCard
        ),
        maxBankTransfer:
          totalPrice -
          state.amountWallet -
          state.amountToPayWithExtracredit -
          state.amountToPayWithCreditCard,
        maxCreditCard: totalPrice - paidByOtherMethods,
      };

      return maxAmounts;
    } else {
      const maxAmounts = {
        maxWallet: walletBalanceValue,
        maxExtracredit: state.extracoinInWallet,
        maxBankTransfer: totalPrice,
        maxCreditCard: totalPrice,
      };

      return maxAmounts;
    }
  }, [
    calculateTotalPrice,
    orderConfig,
    orderItems,
    state.combinePayment,
    state.amountWallet,
    state.amountBankTransfer,
    state.amountToPayWithExtracredit,
    state.amountToPayWithCreditCard,
    state.extracoinInWallet,
  ]);

  const isPaymentMethodAvailable = useCallback(
    (methodType) => {
      if (!state.combinePayment) return true;

      if (state.loading) return true;

      if (methodType === "creditCard" && state.selectedPaymentMethod === "paypal") {
        return false;
      }

      if (methodType === "paypal" && state.selectedPaymentMethod === "creditCard") {
        return false;
      }

      if (methodType === "extracredit" && orderConfig.isLoadingWalletExtracoinBalanceForex)
        return true;

      const totalPrice = calculateTotalPrice();
      const totalPaid =
        state.amountWallet +
        state.amountBankTransfer +
        state.amountToPayWithExtracredit +
        state.amountToPayWithCreditCard;

      if (totalPaid >= totalPrice) {
        switch (methodType) {
          case "wallet":
            return state.amountWallet > 0;
          case "extracredit":
            return state.amountToPayWithExtracredit > 0;
          case "bankTransfer":
            return state.amountBankTransfer > 0;
          case "creditCard":
            return state.amountToPayWithCreditCard > 0;
          case "paypal":
            return state.selectedPaymentMethod === "paypal";
          default:
            return false;
        }
      }

      const maxAmounts = getMaxAmounts();

      switch (methodType) {
        case "wallet":
          return maxAmounts.maxWallet > 0;
        case "extracredit":
          return maxAmounts.maxExtracredit > 0;
        case "bankTransfer":
          return maxAmounts.maxBankTransfer > 0;
        case "creditCard":
          return maxAmounts.maxCreditCard > 0;
        default:
          return true;
      }
    },
    [
      state.combinePayment,
      state.loading,
      state.selectedPaymentMethod,
      state.amountWallet,
      state.amountBankTransfer,
      state.amountToPayWithExtracredit,
      state.amountToPayWithCreditCard,
      calculateTotalPrice,
      getMaxAmounts,
      orderConfig.isLoadingWalletExtracoinBalanceForex,
    ]
  );

  const isTotalCovered = useCallback(() => {
    if (!state.combinePayment) return false;

    const totalPrice = calculateTotalPrice();
    const totalPaid =
      state.amountWallet +
      state.amountBankTransfer +
      state.amountToPayWithExtracredit +
      state.amountToPayWithCreditCard;

    return totalPaid >= totalPrice;
  }, [
    state.combinePayment,
    state.amountWallet,
    state.amountBankTransfer,
    state.amountToPayWithExtracredit,
    state.amountToPayWithCreditCard,
    calculateTotalPrice,
  ]);

  const isPaymentMethodInUse = useCallback(
    (methodType) => {
      if (!state.combinePayment) return false;

      switch (methodType) {
        case "wallet":
          return state.amountWallet > 0;
        case "extracredit":
          return state.amountToPayWithExtracredit > 0;
        case "bankTransfer":
          return state.amountBankTransfer > 0;
        case "creditCard":
          return state.amountToPayWithCreditCard > 0;
        case "paypal":
          return state.selectedPaymentMethod === "paypal";
        default:
          return false;
      }
    },
    [
      state.combinePayment,
      state.amountWallet,
      state.amountBankTransfer,
      state.amountToPayWithExtracredit,
      state.amountToPayWithCreditCard,
      state.selectedPaymentMethod,
    ]
  );

  const setAmountWallet = useCallback(
    (amount) => {
      const maxAmounts = getMaxAmounts();
      const validAmount = Math.min(amount || 0, maxAmounts.maxWallet);

      dispatch({ type: PAYMENT_ACTIONS.SET_AMOUNT_WALLET, payload: validAmount });

      orderConfig.setOrder((prev) => ({ ...prev, amountWallet: validAmount }));

      // Calcular remainingPrice com o novo valor ao invés de usar o state antigo (race condition fix)
      const totalPrice = calculateTotalPrice();
      const totalPaid =
        validAmount + // Usar o novo valor aqui!
        state.amountBankTransfer +
        state.amountToPayWithExtracredit +
        state.amountToPayWithCreditCard;
      const remainingPrice = Math.max(0, totalPrice - totalPaid);

      orderConfig.setPrice(remainingPrice);
    },
    [
      dispatch,
      orderConfig,
      state.amountBankTransfer,
      state.amountToPayWithExtracredit,
      state.amountToPayWithCreditCard,
      calculateTotalPrice,
      getMaxAmounts,
    ]
  );

  const setAmountBankTransfer = useCallback(
    (amount) => {
      const validAmount = amount || 0;

      dispatch({ type: PAYMENT_ACTIONS.SET_AMOUNT_BANK_TRANSFER, payload: validAmount });

      if (orderConfig.setOrder) {
        orderConfig.setOrder((prev) => ({ ...prev, amountBankTransfer: validAmount }));
      }

      if (orderConfig.setPrice) {
        // Calcular remainingPrice com o novo valor ao invés de usar o state antigo (race condition fix)
        const totalPrice = calculateTotalPrice();
        const totalPaid =
          state.amountWallet +
          validAmount + // Usar o novo valor aqui!
          state.amountToPayWithExtracredit +
          state.amountToPayWithCreditCard;
        const remainingPrice = Math.max(0, totalPrice - totalPaid);

        orderConfig.setPrice(remainingPrice);
      }
    },
    [
      dispatch,
      orderConfig,
      state.amountWallet,
      state.amountToPayWithExtracredit,
      state.amountToPayWithCreditCard,
      calculateTotalPrice,
    ]
  );

  const setAmountExtracredit = useCallback(
    (amount) => {
      const maxAmounts = getMaxAmounts();
      const validAmount = Math.min(amount || 0, maxAmounts.maxExtracredit);

      dispatch({ type: PAYMENT_ACTIONS.SET_AMOUNT_EXTRACREDIT, payload: validAmount });

      orderConfig.setOrder((prev) => ({ ...prev, amountToPayWithExtracredit: validAmount }));

      // Calcular remainingPrice com o novo valor ao invés de usar o state antigo (race condition fix)
      const totalPrice = calculateTotalPrice();
      const totalPaid =
        state.amountWallet +
        state.amountBankTransfer +
        validAmount + // Usar o novo valor aqui!
        state.amountToPayWithCreditCard;
      const remainingPrice = Math.max(0, totalPrice - totalPaid);

      orderConfig.setPrice(remainingPrice);
    },
    [
      dispatch,
      orderConfig,
      state.amountWallet,
      state.amountBankTransfer,
      state.amountToPayWithCreditCard,
      calculateTotalPrice,
      getMaxAmounts,
    ]
  );

  const setBankTransferReceipt = useCallback(
    (receipt) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_BANK_TRANSFER_RECEIPT, payload: receipt });
    },
    [dispatch]
  );

  const setCombinePayment = useCallback(
    (combine) => {
      if (state.combinePayment !== combine) {
        dispatch({ type: PAYMENT_ACTIONS.SET_AMOUNT_WALLET, payload: 0 });
        dispatch({ type: PAYMENT_ACTIONS.SET_AMOUNT_BANK_TRANSFER, payload: 0 });
        dispatch({ type: PAYMENT_ACTIONS.SET_AMOUNT_EXTRACREDIT, payload: 0 });
        dispatch({ type: PAYMENT_ACTIONS.SET_AMOUNT_CREDIT_CARD, payload: 0 });
        dispatch({ type: PAYMENT_ACTIONS.SET_BANK_TRANSFER_RECEIPT, payload: null });
        dispatch({ type: PAYMENT_ACTIONS.SET_SELECTED_PAYMENT_METHOD, payload: null });
        dispatch({ type: PAYMENT_ACTIONS.SET_COMBINE_PAYMENT, payload: combine });
      } else {
        dispatch({ type: PAYMENT_ACTIONS.SET_COMBINE_PAYMENT, payload: combine });
      }
    },
    [dispatch, state.combinePayment]
  );

  const setSelectedPaymentMethod = useCallback(
    (method) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_SELECTED_PAYMENT_METHOD, payload: method });
    },
    [dispatch]
  );

  const resetPayments = useCallback(() => {
    dispatch({ type: PAYMENT_ACTIONS.RESET_PAYMENTS });
  }, [dispatch]);

  const setSendMethod = useCallback(
    (method) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_SEND_METHOD, payload: method });
    },
    [dispatch]
  );

  const setAmountCreditCard = useCallback(
    (amount) => {
      const maxAmounts = getMaxAmounts();
      const validAmount = Math.min(amount || 0, maxAmounts.maxCreditCard);

      dispatch({ type: PAYMENT_ACTIONS.SET_AMOUNT_CREDIT_CARD, payload: validAmount });

      orderConfig.setOrder((prev) => ({ ...prev, amountCreditCard: validAmount }));

      // Calcular remainingPrice com o novo valor ao invés de usar o state antigo (race condition fix)
      const totalPrice = calculateTotalPrice();
      const totalPaid =
        state.amountWallet +
        state.amountBankTransfer +
        state.amountToPayWithExtracredit +
        validAmount; // Usar o novo valor aqui!
      const remainingPrice = Math.max(0, totalPrice - totalPaid);

      orderConfig.setPrice(remainingPrice);
    },
    [
      dispatch,
      orderConfig,
      state.amountWallet,
      state.amountBankTransfer,
      state.amountToPayWithExtracredit,
      calculateTotalPrice,
      getMaxAmounts,
    ]
  );

  const setPage = useCallback(
    (pageNumber) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_PAGE, payload: pageNumber });
    },
    [dispatch]
  );

  const setLoading = useCallback(
    (isLoading) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_LOADING, payload: isLoading });
    },
    [dispatch]
  );

  const setAddress = useCallback(
    (address) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_ADDRESS, payload: address });
    },
    [dispatch]
  );

  const setCity = useCallback(
    (city) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_CITY, payload: city });
    },
    [dispatch]
  );

  const setCountry = useCallback(
    (country) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_COUNTRY, payload: country });
    },
    [dispatch]
  );

  const setDistrict = useCallback(
    (district) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_DISTRICT, payload: district });
    },
    [dispatch]
  );

  const setNumber = useCallback(
    (number) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_NUMBER, payload: number });
    },
    [dispatch]
  );

  const setPostalCode = useCallback(
    (postalCode) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_POSTAL_CODE, payload: postalCode });
    },
    [dispatch]
  );

  const setState = useCallback(
    (state) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_STATE, payload: state });
    },
    [dispatch]
  );

  const setCompleteAddress = useCallback(
    (addressData) => {
      dispatch({ type: PAYMENT_ACTIONS.SET_COMPLETE_ADDRESS, payload: addressData });
    },
    [dispatch]
  );

  const maxAmounts = getMaxAmounts();

  const value = useMemo(
    () => ({
      ...state,
      setAmountWallet,
      setAmountBankTransfer,
      setAmountExtracredit,
      setAmountCreditCard,
      setBankTransferReceipt,
      setCombinePayment,
      setSelectedPaymentMethod,
      resetPayments,
      setSendMethod,
      setPage,
      setLoading,
      setAddress,
      setCity,
      setCountry,
      setDistrict,
      setNumber,
      setPostalCode,
      setState,
      setCompleteAddress,
      calculateTotalPrice,
      calculateRemainingPrice,
      getMaxAmounts,
      isPaymentMethodAvailable,
      isTotalCovered,
      isPaymentMethodInUse,
      canProceed,
      order: orderConfig.order,
      currencyProduct: orderConfig.currencyProduct,
      setOrder: orderConfig.setOrder,
      setPrice: orderConfig.setPrice,
      userInfo: orderConfig.userInfo,
      currencyCode: orderConfig.currencyCodeUser,
      currencyFormat: currencyFormat,
      walletBalanceValue: maxAmounts.maxWallet,
      walletExtracoinBalance: orderConfig.walletExtracoinBalance,
      walletExtracoinBalanceForex: orderConfig.walletExtracoinBalanceForex,
      userPercentExtracoin: orderConfig.userPercentExtracoin,
    }),
    [
      state,
      setAmountWallet,
      setAmountBankTransfer,
      setAmountExtracredit,
      setAmountCreditCard,
      setBankTransferReceipt,
      setCombinePayment,
      setSelectedPaymentMethod,
      resetPayments,
      setSendMethod,
      setPage,
      setLoading,
      setAddress,
      setCity,
      setCountry,
      setDistrict,
      setNumber,
      setPostalCode,
      setState,
      setCompleteAddress,
      calculateTotalPrice,
      calculateRemainingPrice,
      getMaxAmounts,
      isPaymentMethodAvailable,
      isTotalCovered,
      isPaymentMethodInUse,
      canProceed,
      orderConfig.order,
      orderConfig.currencyProduct,
      orderConfig.setOrder,
      orderConfig.setPrice,
      orderConfig.userInfo,
      orderConfig.currencyCodeUser,
      currencyFormat,
      maxAmounts.maxWallet,
      orderConfig.walletExtracoinBalance,
      orderConfig.walletExtracoinBalanceForex,
      orderConfig.userPercentExtracoin,
    ]
  );

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
};

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment deve ser usado dentro de PaymentProvider");
  }
  return context;
};

export { PAYMENT_ACTIONS };
